import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, query, where, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import './AddSchool.css';

function AddProspectSchool() {
  const [franchiseeRef, setFranchiseeRef] = useState(null);
  const [existingSchools, setExistingSchools] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [selectedSchoolOption, setSelectedSchoolOption] = useState('');
  const [selectedSchoolData, setSelectedSchoolData] = useState(null);
  const [existingEvents, setExistingEvents] = useState([]);
  
  // Form states
  const [includeDemo, setIncludeDemo] = useState(true);
  const [includeParentsMeeting, setIncludeParentsMeeting] = useState(false);
  const [editingDemoId, setEditingDemoId] = useState(null);
  const [editingMeetingId, setEditingMeetingId] = useState(null);
  
  const [formData, setFormData] = useState({
    schoolName: '',
    address: '',
    contactPerson: '',
    contactPhone: '',
    contactEmail: '',
    demoDate: '',
    demoTime: '',
    expectedStudents: '',
    demoNotes: '',
    meetingDate: '',
    meetingTime: '',
    meetingNotes: ''
  });
  
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const user = auth.currentUser;
      if (user) {
        const franchiseeDocRef = doc(db, 'Franchisees', user.uid);
        setFranchiseeRef(franchiseeDocRef);
        await loadExistingSchools(franchiseeDocRef);
      }
    };
    fetchData();
  }, []);

  const loadExistingSchools = async (franchiseeDocRef) => {
    try {
      setLoadingSchools(true);
      
      const q = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeDocRef),
        where('isActive', '==', true)
      );
      
      const snapshot = await getDocs(q);
      const schools = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort: Active first, then alphabetically
      schools.sort((a, b) => {
        if (a.schoolStatus === 'active' && b.schoolStatus !== 'active') return -1;
        if (a.schoolStatus !== 'active' && b.schoolStatus === 'active') return 1;
        return (a.schoolName || '').localeCompare(b.schoolName || '');
      });
      
      setExistingSchools(schools);
    } catch (error) {
      console.error('Error loading schools:', error);
      setMessage('⚠️ Could not load existing schools. You can still add a new school.');
    } finally {
      setLoadingSchools(false);
    }
  };

  const loadExistingEventsForSchool = async (schoolId) => {
    try {
      // Load events from schoolEvents collection
      const q = query(
        collection(db, 'schoolEvents'),
        where('schoolRef', '==', doc(db, 'schools', schoolId)),
        where('franchiseeRef', '==', franchiseeRef)
      );
      
      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setExistingEvents(events);
      
      // Pre-fill form if events exist
      const demoEvent = events.find(e => e.type === 'demo');
      const meetingEvent = events.find(e => e.type === 'parents_meeting');
      
      if (demoEvent) {
        setEditingDemoId(demoEvent.id);
        setIncludeDemo(true);
        setFormData(prev => ({
          ...prev,
          demoDate: demoEvent.date?.toDate?.()?.toISOString?.()?.split('T')[0] || '',
          demoTime: demoEvent.time || '',
          expectedStudents: demoEvent.expectedStudents || '',
          demoNotes: demoEvent.notes || ''
        }));
      }
      
      if (meetingEvent) {
        setEditingMeetingId(meetingEvent.id);
        setIncludeParentsMeeting(true);
        setFormData(prev => ({
          ...prev,
          meetingDate: meetingEvent.date?.toDate?.()?.toISOString?.()?.split('T')[0] || '',
          meetingTime: meetingEvent.time || '',
          meetingNotes: meetingEvent.notes || ''
        }));
      }
      
    } catch (error) {
      console.error('Error loading events:', error);
      setExistingEvents([]);
    }
  };

  const handleSchoolSelection = async (e) => {
    const value = e.target.value;
    setSelectedSchoolOption(value);
    setExistingEvents([]);
    setEditingDemoId(null);
    setEditingMeetingId(null);
    
    if (value && value !== '__add_new__') {
      // Existing school selected
      const school = existingSchools.find(s => s.id === value);
      if (school) {
        setSelectedSchoolData(school);
        setFormData(prev => ({
          ...prev,
          schoolName: school.schoolName,
          address: school.address || school.prospectInfo?.address || '',
          contactPerson: school.contactPerson || school.prospectInfo?.contactPerson || '',
          contactPhone: school.contactPhone || school.prospectInfo?.contactPhone || '',
          contactEmail: school.contactEmail || school.prospectInfo?.contactEmail || '',
          demoDate: '',
          demoTime: '',
          expectedStudents: '',
          demoNotes: '',
          meetingDate: '',
          meetingTime: '',
          meetingNotes: ''
        }));
        
        // Load existing events for this school
        await loadExistingEventsForSchool(value);
      }
    } else if (value === '__add_new__') {
      // Adding new school
      setSelectedSchoolData(null);
      setFormData({
        schoolName: '',
        address: '',
        contactPerson: '',
        contactPhone: '',
        contactEmail: '',
        demoDate: '',
        demoTime: '',
        expectedStudents: '',
        demoNotes: '',
        meetingDate: '',
        meetingTime: '',
        meetingNotes: ''
      });
      setIncludeDemo(true);
      setIncludeParentsMeeting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!franchiseeRef) {
      setMessage('Error: Franchisee reference not found');
      return;
    }

    if (!selectedSchoolOption) {
      setMessage('Please select a school or choose to add a new one');
      return;
    }

    if (!includeDemo && !includeParentsMeeting) {
      setMessage('Please select at least one event type (Demo or Parents Meeting)');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      let schoolId = null;
      let schoolRef = null;
      
      // STEP 1: Handle school creation/selection
      if (selectedSchoolOption === '__add_new__') {
        // Creating new prospect school
        if (!formData.schoolName.trim()) {
          setMessage('Please enter a school name');
          setLoading(false);
          return;
        }
        
        // Check for duplicate
        const duplicateCheck = existingSchools.find(
          s => s.schoolName.toLowerCase().trim() === formData.schoolName.toLowerCase().trim()
        );
        
        if (duplicateCheck) {
          setMessage(`❌ School "${formData.schoolName}" already exists! Please select it from the dropdown.`);
          setLoading(false);
          return;
        }
        
        // Create new prospect school
        const schoolData = {
          franchiseeRef: franchiseeRef,
          schoolName: formData.schoolName.trim(),
          schoolStatus: 'prospect',
          isActive: true,
          address: formData.address || '',
          contactPerson: formData.contactPerson || '',
          contactPhone: formData.contactPhone || '',
          contactEmail: formData.contactEmail || '',
          createdAt: new Date()
        };

        const newSchoolRef = await addDoc(collection(db, 'schools'), schoolData);
        schoolId = newSchoolRef.id;
        schoolRef = newSchoolRef;
        
      } else {
        // Using existing school
        schoolId = selectedSchoolOption;
        schoolRef = doc(db, 'schools', schoolId);
      }
      
      // STEP 2: Handle Demo
      if (includeDemo) {
        if (!formData.demoDate || !formData.demoTime) {
          setMessage('Please fill in demo date and time');
          setLoading(false);
          return;
        }
        
        const demoData = {
          schoolRef: schoolRef,
          schoolName: formData.schoolName,
          franchiseeRef: franchiseeRef,
          type: 'demo',
          date: new Date(formData.demoDate + 'T' + formData.demoTime),
          time: formData.demoTime,
          expectedStudents: parseInt(formData.expectedStudents) || 0,
          notes: formData.demoNotes || '',
          status: 'scheduled',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        if (editingDemoId) {
          // Update existing demo
          await updateDoc(doc(db, 'schoolEvents', editingDemoId), demoData);
        } else {
          // Create new demo
          await addDoc(collection(db, 'schoolEvents'), demoData);
        }
      }
      
      // STEP 3: Handle Parents Meeting
      if (includeParentsMeeting) {
        if (!formData.meetingDate || !formData.meetingTime) {
          setMessage('Please fill in meeting date and time');
          setLoading(false);
          return;
        }
        
        const meetingData = {
          schoolRef: schoolRef,
          schoolName: formData.schoolName,
          franchiseeRef: franchiseeRef,
          type: 'parents_meeting',
          date: new Date(formData.meetingDate + 'T' + formData.meetingTime),
          time: formData.meetingTime,
          notes: formData.meetingNotes || '',
          status: 'scheduled',
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        if (editingMeetingId) {
          // Update existing meeting
          await updateDoc(doc(db, 'schoolEvents', editingMeetingId), meetingData);
        } else {
          // Create new meeting
          await addDoc(collection(db, 'schoolEvents'), meetingData);
        }
      }
      
      // Success message
      let successMsg = '✅ ';
      if (selectedSchoolOption === '__add_new__') {
        successMsg += `New school "${formData.schoolName}" created! `;
      }
      if (includeDemo && includeParentsMeeting) {
        successMsg += editingDemoId || editingMeetingId ? 'Events updated!' : 'Demo and Parents Meeting scheduled!';
      } else if (includeDemo) {
        successMsg += editingDemoId ? 'Demo updated!' : 'Demo scheduled!';
      } else if (includeParentsMeeting) {
        successMsg += editingMeetingId ? 'Parents Meeting updated!' : 'Parents Meeting scheduled!';
      }
      
      setMessage(successMsg);
      
      // Reload schools list
      await loadExistingSchools(franchiseeRef);
      
      // Reset form
      setSelectedSchoolOption('');
      setSelectedSchoolData(null);
      setExistingEvents([]);
      setEditingDemoId(null);
      setEditingMeetingId(null);
      setIncludeDemo(true);
      setIncludeParentsMeeting(false);
      setFormData({
        schoolName: '',
        address: '',
        contactPerson: '',
        contactPhone: '',
        contactEmail: '',
        demoDate: '',
        demoTime: '',
        expectedStudents: '',
        demoNotes: '',
        meetingDate: '',
        meetingTime: '',
        meetingNotes: ''
      });

    } catch (error) {
      console.error('Error scheduling events:', error);
      setMessage('❌ Error scheduling events. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId, eventType) => {
    if (!window.confirm(`Are you sure you want to delete this ${eventType}?`)) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'schoolEvents', eventId));
      setMessage(`✅ ${eventType === 'demo' ? 'Demo' : 'Parents Meeting'} deleted successfully`);
      
      // Reload events
      if (selectedSchoolOption && selectedSchoolOption !== '__add_new__') {
        await loadExistingEventsForSchool(selectedSchoolOption);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      setMessage('❌ Error deleting event');
    }
  };

  return (
    <div className="add-school-container">
      <div className="add-school-form">
        <div className="form-header">
          <div>
            <h1>Schedule Demo & Parents Meeting 🟡</h1>
            <p className="subtitle">Schedule events at a school</p>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="school-form">
          {/* SCHOOL SELECTION */}
          <div className="form-section">
            <h3>Select School</h3>
            
            <div className="form-group">
              <label htmlFor="schoolSelection">
                School *
                {loadingSchools && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>(Loading...)</span>}
              </label>
              <select
                id="schoolSelection"
                value={selectedSchoolOption}
                onChange={handleSchoolSelection}
                required
                disabled={loadingSchools}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '15px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">-- Select a school --</option>
                
                <option 
                  value="__add_new__"
                  style={{ fontWeight: 'bold', color: '#F59E0B' }}
                >
                  ⚠️ ADD NEW SCHOOL (Check list first!)
                </option>
                
                {existingSchools.filter(s => s.schoolStatus === 'active').length > 0 && (
                  <optgroup label="--- My Active Schools ---">
                    {existingSchools
                      .filter(s => s.schoolStatus === 'active')
                      .map(school => (
                        <option key={school.id} value={school.id}>
                          {school.schoolName}
                        </option>
                      ))}
                  </optgroup>
                )}
                
                {existingSchools.filter(s => s.schoolStatus === 'prospect').length > 0 && (
                  <optgroup label="--- Prospect Schools ---">
                    {existingSchools
                      .filter(s => s.schoolStatus === 'prospect')
                      .map(school => (
                        <option key={school.id} value={school.id}>
                          {school.schoolName} 🟡
                        </option>
                      ))}
                  </optgroup>
                )}
              </select>
              
              {existingSchools.length === 0 && !loadingSchools && (
                <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                  No schools found. Select "Add New School" to create your first school.
                </small>
              )}
            </div>
          </div>

          {/* SHOW EXISTING EVENTS IF ANY */}
          {selectedSchoolOption && selectedSchoolOption !== '__add_new__' && existingEvents.length > 0 && (
            <div className="form-section" style={{
              background: '#F0F9FF',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #3B82F6'
            }}>
              <h3 style={{ color: '#1E40AF', marginBottom: '12px' }}>📅 Existing Events</h3>
              {existingEvents.map(event => (
                <div key={event.id} style={{
                  background: 'white',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <strong>{event.type === 'demo' ? '🎨 Demo' : '👥 Parents Meeting'}</strong>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {event.date?.toDate?.()?.toLocaleDateString()} at {event.time}
                      {event.expectedStudents > 0 && ` • ${event.expectedStudents} students`}
                    </div>
                    {event.notes && <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>{event.notes}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteEvent(event.id, event.type)}
                    style={{
                      padding: '6px 12px',
                      background: '#EF4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ONLY SHOW FORMS IF SCHOOL SELECTED */}
          {selectedSchoolOption && (
            <>
              {/* NEW SCHOOL INFORMATION */}
              {selectedSchoolOption === '__add_new__' && (
                <div className="form-section" style={{
                  background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
                  padding: '20px',
                  borderRadius: '12px',
                  border: '2px solid #F59E0B'
                }}>
                  <h3 style={{ color: '#92400E', marginBottom: '16px' }}>
                    🆕 New School Details
                  </h3>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="schoolName">School Name *</label>
                      <input
                        type="text"
                        id="schoolName"
                        name="schoolName"
                        value={formData.schoolName}
                        onChange={handleChange}
                        placeholder="e.g., Greenwood Primary School"
                        required
                        style={{ backgroundColor: 'white' }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="address">Address</label>
                      <input
                        type="text"
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="123 Main St, Porto"
                        style={{ backgroundColor: 'white' }}
                      />
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="contactPerson">Contact Person</label>
                      <input
                        type="text"
                        id="contactPerson"
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={handleChange}
                        placeholder="Principal's name"
                        style={{ backgroundColor: 'white' }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="contactPhone">Phone</label>
                      <input
                        type="tel"
                        id="contactPhone"
                        name="contactPhone"
                        value={formData.contactPhone}
                        onChange={handleChange}
                        placeholder="+351 123 456 789"
                        style={{ backgroundColor: 'white' }}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="contactEmail">Email</label>
                    <input
                      type="email"
                      id="contactEmail"
                      name="contactEmail"
                      value={formData.contactEmail}
                      onChange={handleChange}
                      placeholder="principal@school.com"
                      style={{ backgroundColor: 'white' }}
                    />
                  </div>
                </div>
              )}

              {/* DEMO SECTION */}
              <div className="form-section">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <input
                    type="checkbox"
                    id="includeDemo"
                    checked={includeDemo}
                    onChange={(e) => setIncludeDemo(e.target.checked)}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  <label htmlFor="includeDemo" style={{ margin: 0, cursor: 'pointer', fontSize: '16px', fontWeight: '600' }}>
                    🎨 Schedule Demo {editingDemoId && '(Editing)'}
                  </label>
                </div>
                
                {includeDemo && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="demoDate">Demo Date *</label>
                        <input
                          type="date"
                          id="demoDate"
                          name="demoDate"
                          value={formData.demoDate}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="demoTime">Demo Time *</label>
                        <input
                          type="time"
                          id="demoTime"
                          name="demoTime"
                          value={formData.demoTime}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="expectedStudents">Expected Students</label>
                        <input
                          type="number"
                          id="expectedStudents"
                          name="expectedStudents"
                          value={formData.expectedStudents}
                          onChange={handleChange}
                          min="0"
                          placeholder="20"
                        />
                      </div>
                      <div className="form-group">
                        {/* Empty for layout */}
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="demoNotes">Demo Notes</label>
                      <textarea
                        id="demoNotes"
                        name="demoNotes"
                        value={formData.demoNotes}
                        onChange={handleChange}
                        rows="2"
                        placeholder="Any notes about the demo..."
                      />
                    </div>
                  </>
                )}
              </div>

              {/* PARENTS MEETING SECTION */}
              <div className="form-section">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <input
                    type="checkbox"
                    id="includeParentsMeeting"
                    checked={includeParentsMeeting}
                    onChange={(e) => setIncludeParentsMeeting(e.target.checked)}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  <label htmlFor="includeParentsMeeting" style={{ margin: 0, cursor: 'pointer', fontSize: '16px', fontWeight: '600' }}>
                    👥 Schedule Parents Meeting/Evening {editingMeetingId && '(Editing)'}
                  </label>
                </div>
                
                {includeParentsMeeting && (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="meetingDate">Meeting Date *</label>
                        <input
                          type="date"
                          id="meetingDate"
                          name="meetingDate"
                          value={formData.meetingDate}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="meetingTime">Meeting Time *</label>
                        <input
                          type="time"
                          id="meetingTime"
                          name="meetingTime"
                          value={formData.meetingTime}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="meetingNotes">Meeting Notes</label>
                      <textarea
                        id="meetingNotes"
                        name="meetingNotes"
                        value={formData.meetingNotes}
                        onChange={handleChange}
                        rows="2"
                        placeholder="Any notes about the parents meeting..."
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading ? 'Saving...' : (editingDemoId || editingMeetingId ? '💾 Update Events' : '📅 Schedule Events')}
                </button>
              </div>
            </>
          )}

          {message && (
            <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default AddProspectSchool;